import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, CheckCircle2, AlertTriangle } from "lucide-react";
import { Card } from "../../../../components/ui/Card";
import { showToast } from "../../../../utils/showToast";

/**
 * Página a la que vuelve el navegador cuando el backend terminó de instalar la
 * app de Beartrack en la tienda Wix. El backend agrega ?integration=wix y
 * ?status=success|error y, según el caso, ?alias=nombreTienda o ?error=code.
 *
 * Espejo de TiendaNubeCallback, con UNA diferencia de fondo: en Wix no hay
 * authorization-code. El navegador viene del app-installer de Wix, el backend
 * ya validó el `signedInstance` y guardó la integración; acá no queda nada que
 * intercambiar, solo informar el resultado.
 *
 * Por qué existe la página en vez de volver directo a /home/integrations: esa
 * vista ya lee ?error= para el retorno de MercadoLibre, así que un fallo de Wix
 * (?error=installation_failed) dispararía el toast de MercadoLibre.
 */
export default function WixCallback() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [status, setStatus] = useState("processing");

  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    const resolvedStatus = params.get("status") || "processing";
    const alias = params.get("alias");
    const errorCode = params.get("error");

    setStatus(resolvedStatus);

    if (resolvedStatus === "success") {
      showToast(
        alias
          ? t('Tienda Wix "{{alias}}" vinculada correctamente', { alias })
          : t("Tienda Wix vinculada correctamente"),
        true,
      );
    } else if (resolvedStatus === "error") {
      showToast(
        errorCode
          ? t("No se pudo vincular Wix ({{error}})", { error: errorCode })
          : t("No se pudo vincular Wix"),
        false,
      );
    }

    const timer = setTimeout(() => {
      navigate("/home/integrations", { replace: true });
    }, 1500);
    return () => clearTimeout(timer);
  }, [navigate, t]);

  return (
    <div className="flex justify-center items-center min-h-[60vh] px-4">
      <Card className="w-full max-w-[420px] text-center">
        {status === "processing" && (
          <>
            <Loader2 className="w-10 h-10 mx-auto text-brand-500 animate-spin" aria-hidden="true" />
            <p className="mt-3 mb-0 text-sm text-fg">{t("Procesando vinculación con Wix…")}</p>
          </>
        )}
        {status === "success" && (
          <>
            <CheckCircle2 className="w-12 h-12 mx-auto text-status-success" aria-hidden="true" />
            <p className="mt-3 mb-0 text-sm text-fg">{t("Tienda Wix vinculada. Redirigiendo…")}</p>
          </>
        )}
        {status === "error" && (
          <>
            <AlertTriangle className="w-12 h-12 mx-auto text-status-error" aria-hidden="true" />
            <p className="mt-3 mb-0 text-sm text-fg">{t("No se pudo completar la vinculación. Redirigiendo…")}</p>
          </>
        )}
      </Card>
    </div>
  );
}
